import { useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/data-display/Badge';
import {
  type ConveraApiLogDto,
  type ConveraApiLogDetailDto,
  JSON_TRUNCATION_THRESHOLD,
  getStatusVariant,
  getDirectionDisplay,
  sanitiseJsonString,
} from './types';
import { fetchLogById } from './useConveraLogs';

interface LogDetailModalProps {
  log: ConveraApiLogDto | null;
  onClose: () => void;
}

// String-level redaction for the truncated preview. We can't parse the
// slice as JSON (it's cut mid-token), so we redact by regex over the
// in-window text. Mirrors the key set in `sanitiseJson` from types.ts:
// authorization, password, client_secret, api_key, *_token. This is a
// debugging aid, not a security boundary -- the trusted path is the
// backend sanitiser. The frontend redacts as defence in depth so a
// historical row written before the backend sanitiser shipped still
// has its visible secrets masked.
const SENSITIVE_KEY_RE =
  /"((?:\w+_)?token|authorization|password|client_secret|api_key)"\s*:\s*"[^"]*"/gi;
function redactSensitiveSpans(slice: string): string {
  return slice.replace(SENSITIVE_KEY_RE, (_match, key: string) => `"${key}": "[REDACTED]"`);
}

// Threshold-aware body renderer. Truncates the *raw* JSON string at the
// threshold BEFORE handing it to the sanitiser, so a 5MB Convera response
// pays at most threshold-many characters of CPU instead of four full
// traversals (stringify -> parse -> recursive walk -> stringify) over
// the whole body. The un-truncated path still goes through
// sanitiseJsonString -- the trusted whole-body redactor.
function renderBody(body: unknown): string {
  if (body === null || body === undefined) return '—';

  let raw: string;
  try {
    raw = JSON.stringify(body, null, 2);
  } catch {
    // Circular reference or BigInt -- surface a marker instead of crashing
    // the modal. The detail endpoint should never produce these but
    // historical rows are out of our control.
    return '[unstringifiable body]';
  }

  if (raw.length <= JSON_TRUNCATION_THRESHOLD) {
    return sanitiseJsonString(raw) ?? raw;
  }

  const slice = raw.slice(0, JSON_TRUNCATION_THRESHOLD);
  return `${redactSensitiveSpans(slice)}\n… (truncated)`;
}

export function LogDetailModal({ log, onClose }: LogDetailModalProps) {
  const [detail, setDetail] = useState<ConveraApiLogDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<Error | null>(null);

  useEffect(() => {
    if (!log) {
      // Modal closed — reset for next open.
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }

    const controller = new AbortController();
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);

    fetchLogById(log.convera_api_log_id, controller.signal)
      .then((d) => {
        if (!controller.signal.aborted) setDetail(d);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // Axios maps abort to a "canceled" error too; ignore both shapes.
        const name = (err as { name?: string } | null)?.name;
        if (name === 'AbortError' || name === 'CanceledError') return;
        setDetailError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });

    return () => controller.abort();
  }, [log]);

  // Memoise the rendered bodies so unrelated modal re-renders (focus,
  // hover, etc.) don't re-pay the JSON.stringify + sanitise cost. Keyed
  // on the body reference, which is fresh per detail fetch.
  const requestBodyText = useMemo(() => renderBody(detail?.request_body), [detail?.request_body]);
  const responseBodyText = useMemo(
    () => renderBody(detail?.response_body),
    [detail?.response_body],
  );

  if (!log) return null;

  const dir = getDirectionDisplay(log.direction);
  const statusVariant = getStatusVariant(log.response_status);
  const timestamp = new Date(log.created_at).toLocaleString();

  return (
    <Modal
      isOpen={!!log}
      title="Log Detail"
      description={`Convera API log #${log.convera_api_log_id} — ${log.request_method} ${log.request_url}`}
      onClose={onClose}
      className="max-w-2xl"
      footer={
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="log-detail-close">
          Close
        </Button>
      }
    >
      <div className="space-y-3" data-testid="log-detail-content">
        <div className="grid grid-cols-2 gap-3 text-[13px]">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Timestamp
            </span>
            <div className="mt-0.5 font-mono text-[12px] text-[#333]">{timestamp}</div>
          </div>
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Direction
            </span>
            <div className="mt-0.5">
              <Badge variant={dir.variant}>
                {dir.icon} {dir.label}
              </Badge>
            </div>
          </div>
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Method
            </span>
            <div className="mt-0.5 font-semibold text-[12px] text-[#333]">{log.request_method}</div>
          </div>
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Status Code
            </span>
            <div className="mt-0.5">
              <Badge variant={statusVariant}>{log.response_status ?? '—'}</Badge>
            </div>
          </div>
          <div className="col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Endpoint
            </span>
            <div className="mt-0.5 break-all font-mono text-[12px] text-[#1a3a5c]">
              {log.request_url}
            </div>
          </div>
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Duration
            </span>
            <div className="mt-0.5 font-mono text-[12px] text-[#333]">
              {log.duration_ms === null ? '—' : `${log.duration_ms} ms`}
            </div>
          </div>
          {log.resource_type && (
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
                Resource
              </span>
              <div className="mt-0.5 text-[12px] text-[#333]">
                {log.resource_type}
                {log.resource_id ? ` (${log.resource_id})` : ''}
              </div>
            </div>
          )}
          {log.convera_ref && (
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
                Convera Ref
              </span>
              <div className="mt-0.5 font-mono text-[12px] text-[#333]">{log.convera_ref}</div>
            </div>
          )}
          {log.error_message && (
            <div className="col-span-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
                Error
              </span>
              <div className="mt-0.5 text-[12px] text-[#c62828]">{log.error_message}</div>
            </div>
          )}

          {detailError && (
            <div
              className="col-span-2 rounded border border-[#ffcdd2] bg-[#ffebee] px-3 py-2 text-[12px] text-[#c62828]"
              data-testid="log-detail-fetch-error"
            >
              Failed to load body
            </div>
          )}

          <details className="col-span-2" data-testid="log-detail-request-body">
            <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Request body
            </summary>
            <div className="mt-2 max-h-64 overflow-auto rounded bg-[#fafafa] p-2 font-mono text-[11px] text-[#333]">
              {detailLoading ? (
                <LoadingSpinner size="sm" />
              ) : detailError ? (
                <span className="text-[#c62828]">Failed to load body</span>
              ) : detail?.request_body == null ? (
                '—'
              ) : (
                <pre>{requestBodyText}</pre>
              )}
            </div>
          </details>

          <details className="col-span-2" data-testid="log-detail-response-body">
            <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Response body
            </summary>
            <div className="mt-2 max-h-64 overflow-auto rounded bg-[#fafafa] p-2 font-mono text-[11px] text-[#333]">
              {detailLoading ? (
                <LoadingSpinner size="sm" />
              ) : detailError ? (
                <span className="text-[#c62828]">Failed to load body</span>
              ) : detail?.response_body == null ? (
                '—'
              ) : (
                <pre>{responseBodyText}</pre>
              )}
            </div>
          </details>
        </div>
      </div>
    </Modal>
  );
}
